import { useEffect, useState } from 'react';
import { GridToolbarContainer } from '@mui/x-data-grid-premium';
import { Box, Chip, IconButton, Paper, Tooltip } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import CancelIcon from '@mui/icons-material/Cancel';

import { dataGridPremiumColumns } from '../../../utils/helper';
import { DataGridIdentifiers, AddButtonUi, ConfirmDialogUI, DatagridPremiumUi } from '../../Interface';
import ProductionReceiptDialog from './ProductionReceiptDialog';
import { InvManagementMethod } from '../../../config/itemGroup';
import ItemBatchLabelDialog from '../../Item/ItemBatchLabelDialog';
import BatchSnDialog from '../../BatchSn/BatchSnDialog';
import BatchSnLink from '../../BatchSn/BatchSnLink';
import documentService from '../../../service/newDoc.service';
import productionService from '../../../service/production.service';
import { DocumentType } from '../../../config/DocumentType';
import { DocStatus } from '../../../config/document';
import { extractCopyPayload } from '../../../utils/DocHelper';
import addNotification from '../../../utils/addNotification';
import { NotificationType } from '../../../config';

const EditToolbar = (props) => {
  const { disabled, setDialogOpen } = props;

  const handleClick = async () => {
    setDialogOpen(true);
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
};

const ProductionReceipt = (props) => {
  const { selectedValues, setSelectedValues, setRefetchTable, disabled } = props;
  const [reports, setReports] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [labelDialogState, setLabelDialogState] = useState({ open: false, row: null });
  const [batchSnDialogRow, setBatchSnDialogRow] = useState(null);
  const [receiptToStorno, setReceiptToStorno] = useState(null);

  const handleReceiptStorno = async () => {
    try {
      const receiptDocument = await documentService.getDocumentById(
        receiptToStorno.docId,
        DocumentType.ReceiptFromProduction
      );
      const stornoPayload = extractCopyPayload(receiptDocument, null, {}, '', null, {
        includeClosedLinesForStorno: true,
      });

      await documentService.docStorno(receiptToStorno.docId, DocumentType.ReceiptFromProduction, stornoPayload);

      const updatedProduction = await productionService.getProductionById(selectedValues.id);
      setSelectedValues(updatedProduction);
      setReports(updatedProduction?.receipts ?? []);
      setRefetchTable(true);
      setReceiptToStorno(null);
      addNotification({
        content: 'A gyártási bevételezés stornózása sikeres.',
        type: NotificationType.SUCCESS,
      });
    } catch (_error) {
      addNotification({
        content: 'A gyártási bevételezés stornózása nem sikerült.',
        type: NotificationType.ERROR,
      });
    }
  };

  const columns = dataGridPremiumColumns([
    {
      field: 'docNumber',
      headerName: 'Biz. szám',
      type: 'string',
      valueGetter: (_data, row) => row.doc.docNum,
    },
    {
      field: 'itemCode',
      headerName: 'Cikkszám',
      type: 'string',
    },
    {
      field: 'itemName',
      headerName: 'Cikknév',
      type: 'string',
    },
    {
      field: 'batchSn',
      headerName: 'Sarzs',
      type: 'string',
      renderCell: (data) => <BatchSnLink row={data.row} onOpen={setBatchSnDialogRow} />,
    },
    {
      field: 'qty',
      headerName: 'Mennyiség',
      type: 'string',
      filterable: false,
      renderCell: (data) => {
        let quantity = data.row.qty;

        if (data.row.item.invManagementMethod === InvManagementMethod.BATCH) quantity = data.row.batchQty;
        if (data.row.doc?.docStatus === DocStatus.CANCELLATION) quantity = -Math.abs(Number(quantity));
        return quantity;
      },
    },
    {
      field: 'quantityUnitCode',
      headerName: 'Me.',
      type: 'string',
      filterable: false,
      sortable: false,
    },
    {
      field: 'actions',
      headerName: '',
      type: 'string',
      filterable: false,
      sortable: false,
      width: 60,
      renderCell: (data) => {
        const isBatch = data.row.item?.invManagementMethod === InvManagementMethod.BATCH;
        if (!isBatch) return null;
        return (
          <IconButton size="small" onClick={() => setLabelDialogState({ open: true, row: data.row })}>
            <PrintIcon fontSize="small" />
          </IconButton>
        );
      },
    },
    {
      field: 'storno',
      headerName: 'Stornó',
      type: 'string',
      filterable: false,
      sortable: false,
      width: 150,
      renderCell: (data) => {
        const docStatus = data.row.doc?.docStatus;

        if (docStatus === DocStatus.CANCELLATION) {
          return (
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', my: 0.5 }}>
              <Chip label="Stornó" color="error" size="small" />
            </Box>
          );
        }
        if (docStatus === DocStatus.CANCELED || data.row.doc?.stornoId) {
          return (
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', my: 0.5 }}>
              <Chip label="Stornózott" color="error" size="small" variant="outlined" />
            </Box>
          );
        }
        if (docStatus !== DocStatus.CLOSED) return 'Nem stornózható';

        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Tooltip title="Stornózás">
              <IconButton
                size="small"
                color="error"
                aria-label="Stornózás"
                onClick={() => setReceiptToStorno(data.row)}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ]);

  useEffect(() => {
    setReports(selectedValues?.receipts ?? []);
  }, [selectedValues]);

  return (
    <>
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Paper elevation={2} sx={{ overflow: 'hidden', boxShadow: 3, borderRadius: 2, width: '100%' }}>
            <DatagridPremiumUi
              datagridIdentifier={DataGridIdentifiers.ProductionReceipt}
              height={480}
              columns={columns}
              rows={reports}
              slots={{
                toolbar: EditToolbar,
              }}
              slotProps={{
                toolbar: {
                  disabled,
                  setDialogOpen,
                },
              }}
              headerFilters={false}
              getRowHeight={() => 'auto'}
              getRowId={(row) => `${row.docId}_${row.lineNum}`}
            />
          </Paper>
        </Box>
      </Box>
      <ProductionReceiptDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={(data) => {
          setReports((prev) => [...prev, data]);
          setDialogOpen(false);
        }}
        {...props}
      />
      <ItemBatchLabelDialog
        open={labelDialogState.open}
        onClose={() => setLabelDialogState({ open: false, row: null })}
        initialBatch={labelDialogState.row?.batchSn ?? ''}
        readonlyBatch
        selectedValuesState={{
          selectedValues: {
            itemCode: labelDialogState.row?.itemCode ?? '',
            itemName: labelDialogState.row?.itemName ?? '',
            quantityUnit: labelDialogState.row?.item?.quantityUnit ?? null,
            barCode: labelDialogState.row?.item?.barCode ?? '',
          },
          setSelectedValues: () => {},
        }}
      />
      <BatchSnDialog
        open={Boolean(batchSnDialogRow)}
        onClose={() => setBatchSnDialogRow(null)}
        row={batchSnDialogRow}
      />
      <ConfirmDialogUI
        show={Boolean(receiptToStorno)}
        onHide={() => setReceiptToStorno(null)}
        handleChangeElement={handleReceiptStorno}
        title="Gyártási bevételezés stornózása"
        message="Biztosan stornózza a gyártási bevételezést? A művelet nem vonható vissza."
        confirmButtonText="Stornózás"
      />
    </>
  );
};

export default ProductionReceipt;
